/**
 * Annotate entry point + save orchestration, shared by every image-producing node.
 *
 * Node integration is two lines: spread `toolbarAction` into the node's
 * `AgentNodeToolbar` actions, and render `<AnnotationEditorOverlay>` when
 * `isOpen`. Everything else — eligibility, port resolution, the POST, the node
 * and edge writes, the NAD trace — lives here so the six call sites cannot drift.
 *
 * SAVE PATH (RFC-1115 §2). The REST route deliberately does NOT create a node;
 * it bakes pixels and returns an asset path. Structural graph changes stay on
 * the console's direct-to-YDoc path:
 *
 *   1. POST /projects/{id}/assets/annotate  ->  { path, aspect_ratio, ... }
 *   2. canvas.addNode('image', ...)         ->  persists via addNodeToYDoc
 *   3. canvas.replacePortEdges(new, 'in_image', [{ source, sourceHandle }])
 *
 * Step 3 uses `replacePortEdges` — an EXISTING persisted primitive (it writes
 * the YDoc edge map inside a transact and then calls `flushResolvedPortData`),
 * already used by the reference selectors. Note it is NOT `canvasContext.addEdge`,
 * which only calls `setEdges` and would leave the wire in browser memory only,
 * correct on screen and absent after a reload.
 *
 * Step 2 must not seed `in_image` in the node data: `image.in_image` is
 * edge-derived, so `assertNoDirectAssetInputPortWrites` rejects a direct write.
 * The value arrives from the edge flush in step 3.
 */

import { createElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Highlighter } from 'lucide-react'

import { api, ApiRequestError } from '../../../../../api/client'
import { createNadDebug } from '../../../../../utils/debugLog'
import type { ImageAnnotationDict } from '../../../../../generated/nodeDataTypes'
import { useCanvasOptional } from '../../../CanvasContext'
import {
  findNonOverlappingCanvasPosition,
  resolveAbsoluteCanvasPosition,
} from '../../../canvasPosition'
import type { ToolbarAction } from '../AgentNodeToolbar'
import { createUploadAssetRef } from '../uploadAssetRef'
import { buildAnnotatedNodeLabel } from './annotationNodeIdentity'
import { resolveImageAnnotationActionState } from './imageAnnotationAccess'
import { singleImageOutputPort } from './imageOutputPort'

const saveLog = createNadDebug('dw:image-annotation:save')
const accessLog = createNadDebug('dw:image-annotation:access')

/** Horizontal gap between the source node and the annotated one. */
const ANNOTATED_NODE_GAP_PX = 60
/** Fallback width when the source node has not been measured yet. */
const FALLBACK_NODE_WIDTH_PX = 320

export interface UseImageAnnotationArgs {
  /** Host node id — the annotation source. */
  nodeId: string
  /** Frontend node type, used to resolve the image output port from schemas. */
  feNodeType: string
  /** Project-relative asset path of the image to annotate (the bake's input). */
  imagePath: string | null | undefined
  /** Displayable URL for the same asset (the editor's underlay). */
  imageUrl: string | null | undefined
  projectId: string | null | undefined
}

export interface UseImageAnnotationResult {
  toolbarAction: ToolbarAction
  isOpen: boolean
  isSaving: boolean
  traceId: string
  close: () => void
  save: (annotations: ImageAnnotationDict[]) => void
}

/** Mirrors the backend's `annot_<hex8>` so one bake is one chain across surfaces. */
function makeTraceId(): string {
  const random = Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, '0')
  return `annot_ui_${random}`
}

export function useImageAnnotation({
  nodeId,
  feNodeType,
  imagePath,
  imageUrl,
  projectId,
}: UseImageAnnotationArgs): UseImageAnnotationResult {
  const { t } = useTranslation('canvas')
  // Null outside a CanvasProvider. This feature is additive to nodes that are
  // rendered without one in unit tests, and no canvas simply means there is
  // nowhere to put the annotated node — so the entry point stays hidden.
  const canvas = useCanvasOptional()
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [traceId, setTraceId] = useState(makeTraceId)

  const sourcePort = singleImageOutputPort(feNodeType)
  const actionState = resolveImageAnnotationActionState({
    hasSourcePort: sourcePort !== null,
    hasCanvas: canvas !== null,
    hasProject: !!projectId,
    hasImagePath: !!imagePath,
    hasImageUrl: !!imageUrl,
  })
  const actionVisible = actionState.visible
  const hasUsableAsset = !actionState.disabled

  useEffect(() => {
    accessLog({
      event: 'annotate:access_checked',
      concern: 'access',
      node_id: nodeId,
      access_granted: true,
      access_source: 'all-accounts',
      eligible_source_port: sourcePort !== null,
      action_visible: actionVisible,
      action_disabled: actionVisible && !hasUsableAsset,
      has_usable_asset: hasUsableAsset,
    })
  }, [actionVisible, hasUsableAsset, nodeId, sourcePort])

  const open = useCallback(() => {
    if (!hasUsableAsset) {
      accessLog({
        event: 'annotate:open_ignored',
        concern: 'access',
        node_id: nodeId,
        reason: 'missing-image-asset',
      })
      return
    }
    const nextTraceId = makeTraceId()
    setTraceId(nextTraceId)
    setIsOpen(true)
    accessLog({
      event: 'annotate:lightbox_opened',
      concern: 'editor',
      trace_id: nextTraceId,
      node_id: nodeId,
    })
  }, [hasUsableAsset, nodeId])

  const close = useCallback(() => {
    setIsOpen(false)
    accessLog({
      event: 'annotate:lightbox_closed',
      concern: 'editor',
      trace_id: traceId,
      node_id: nodeId,
      exit: 'explicit-close',
    })
  }, [nodeId, traceId])

  const save = useCallback(
    (annotations: ImageAnnotationDict[]) => {
      // Reentrancy guard. `isSaving` only disables the button once the parent
      // re-renders, so a double-click inside that window would fire two bakes
      // and create two nodes from one user action.
      if (isSaving) return
      if (!projectId || !imagePath || sourcePort === null || canvas === null) return
      if (annotations.length === 0) {
        toast.error(t('annotate.emptySpec'))
        return
      }

      const kinds: Record<string, number> = {}
      for (const element of annotations) {
        kinds[element.kind] = (kinds[element.kind] ?? 0) + 1
      }
      saveLog({
        event: 'annotate:save_requested',
        concern: 'save',
        trace_id: traceId,
        node_id: nodeId,
        element_count: annotations.length,
        elements_by_kind: kinds,
      })

      setIsSaving(true)
      void (async () => {
        try {
          const result = await api.annotateAsset(projectId, imagePath, annotations)

          const sourceNode = canvas.getNode(nodeId)
          if (!sourceNode) {
            throw new Error(
              `Cannot place annotated result: source node "${nodeId}" was not found`,
            )
          }
          const sourceWidth = sourceNode.width ?? FALLBACK_NODE_WIDTH_PX
          const relativePosition = {
            x: sourceWidth + ANNOTATED_NODE_GAP_PX,
            y: 0,
          }
          const preferredPosition = resolveAbsoluteCanvasPosition(
            nodeId,
            relativePosition,
            canvas.getNode,
          )
          const position = findNonOverlappingCanvasPosition(
            'image',
            preferredPosition,
            canvas.getNodes(),
          )
          const label = buildAnnotatedNodeLabel(sourceNode, t('annotate.nodeLabel'))

          saveLog({
            event: 'annotate:placement_resolved',
            concern: 'node-placement',
            trace_id: traceId,
            source_node_id: nodeId,
            source_parent_id: sourceNode.parentId ?? null,
            source_relative_x: sourceNode.position.x,
            source_relative_y: sourceNode.position.y,
            desired_offset_x: relativePosition.x,
            desired_offset_y: relativePosition.y,
            preferred_absolute_x: preferredPosition.x,
            preferred_absolute_y: preferredPosition.y,
            target_absolute_x: position.x,
            target_absolute_y: position.y,
          })

          // `image.out_image` is in FRONTEND_WRITABLE_OUTPUT_MAP_PORTS, which is
          // what makes this console-side write legal. `in_image` is deliberately
          // absent — it is edge-derived and arrives from the flush below.
          const newNodeId = canvas.addNode('image', position, {
            out_image: createUploadAssetRef(result.path, {
              aspectRatio: result.aspect_ratio,
              width: result.width,
              height: result.height,
            }),
            in_file_name: result.filename,
            status: 'completed',
            // Without this the node card shows its raw UUID, which reads as
            // debris on the canvas rather than as the result of an action.
            label,
          })

          canvas.replacePortEdges(newNodeId, 'in_image', [
            { source: nodeId, sourceHandle: sourcePort },
          ])

          saveLog({
            event: 'annotate:node_created',
            concern: 'node-create',
            trace_id: traceId,
            new_node_id: newNodeId,
            source_node_id: nodeId,
            from_port: sourcePort,
            to_port: 'in_image',
            asset_path: result.path,
            node_label: label,
            // Makes the "wire only exists in ReactFlow" failure visible in a
            // trace instead of only after a reload.
            edge_persisted: true,
            target_absolute_x: position.x,
            target_absolute_y: position.y,
          })

          setIsOpen(false)
          toast.success(t('annotate.saveSucceeded'))
        } catch (error) {
          // Surface the backend's own reason verbatim (Dev Principle 7): a
          // rejected coordinate or an unresolvable font is the caller's to fix,
          // and a generic "save failed" hides which of eight elements was bad.
          const status = error instanceof ApiRequestError ? error.status : 0
          const message = error instanceof Error ? error.message : String(error)
          saveLog({
            event: 'annotate:save_failed',
            concern: 'save',
            trace_id: traceId,
            node_id: nodeId,
            status,
            reason: message,
          })
          toast.error(t('annotate.saveFailed', { message }))
        } finally {
          setIsSaving(false)
        }
      })()
    },
    [canvas, imagePath, isSaving, nodeId, projectId, sourcePort, t, traceId],
  )

  const toolbarAction: ToolbarAction = useMemo(
    () => ({
      key: 'annotate',
      label: t('annotate.button'),
      icon: createElement(Highlighter, { size: 14 }),
      onClick: open,
      hidden: !actionVisible,
      disabled: !hasUsableAsset,
      group: true,
    }),
    [actionVisible, hasUsableAsset, open, t],
  )

  return { toolbarAction, isOpen, isSaving, traceId, close, save }
}
